import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import PropertyDetails from "../components/PropertyDetails";
// JPEG, not the source PNG: the artwork is a photograph, which PNG stores badly
// (1.9MB against 300KB at the same visible quality). This page is opened by clients
// on phones, often on cellular, and it is the first thing that loads.
import bannerWide from "../assets/clear-sky-banner-wide.jpg";
import bannerNarrow from "../assets/clear-sky-banner-narrow.jpg";

import PropertyFeatures from "../components/PropertyFeatures";
import RentalValueAnalysis from "../components/RentalValueAnalysis";
import ComparablesSection from "../components/ComparablesSection";
import ImageGallery from "../components/ImageGallery";
import OutOfPocketSection from "../components/OutOfPocketSection";
import AIRentSummary from "../components/AIRentSummary";
import RCRAnalysis from "../components/RCRAnalysis";
import useIsNarrow from "../hooks/useIsNarrow";
import { calculateClosingCosts } from "../utils/closingCostsCalculator";
import { resolveMortgageRates, calculateRentFromMode } from "../utils/rentalAnalysisCalculator";


const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

export default function ClientViewPage() {
  const { token } = useParams();
  // This page is sent to clients by email and is opened on a phone more often than not.
  const narrow = useIsNarrow();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadData = async () => {
    try {
      const resp = await fetch(`${API_URL}/client/share/${token}`);
      if (!resp.ok) {
        if (resp.status === 410) throw new Error("This link has expired");
        if (resp.status === 404) throw new Error("Link not found");
        throw new Error("Failed to load");
      }
      const json = await resp.json();
      setData(json);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={{ textAlign: "center", marginTop: "100px", color: "#666" }}>Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={pageStyle}>
        <div style={{ textAlign: "center", marginTop: "100px" }}>
          <h2 style={{ color: "#d32f2f" }}>{error}</h2>
          <p style={{ color: "#666" }}>Please contact your representative for a new link.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      {/* Header — a full-bleed dark band.
          Two source images, not one cropped one. The supplied artwork is 4:1 with type sized
          for a hero, so at full-bleed on a wide monitor it rendered at ~91% of native and the
          type was enormous; cropping it to a strip then sliced the mark and the tagline in
          half. These are pre-composed instead: the composition sits centred on black at
          12.8:1 for desktop and 4.3:1 for phones, edges faded so the artwork's lighter right
          side never meets a hard black edge.

          No objectFit and no fixed height -- height comes from the aspect ratio, so nothing is
          ever cropped and the type scales with the viewport (200px at 2560, ~113px at 1440,
          ~90px on a 390px phone). */}
      <div style={{ backgroundColor: "#0a0a0a", marginBottom: narrow ? "16px" : "24px" }}>
        <img
          src={narrow ? bannerNarrow : bannerWide}
          alt="ClearSky — NYC real estate investment, brokerage, advisory, property management"
          style={{ display: "block", width: "100%", height: "auto" }}
        />
        {data.client_name && (
          <div style={{ maxWidth: "1200px", margin: "0 auto", padding: narrow ? "0 12px" : "0 30px" }}>
            <p style={{
              margin: 0,
              padding: narrow ? "10px 0" : "12px 0",
              fontSize: narrow ? "13px" : "14px",
              color: "#bbb",
            }}>
              Prepared for {data.client_name}
            </p>
          </div>
        )}
      </div>

      {/* Listings */}
      <div style={{ padding: narrow ? "0 12px" : "0 30px", maxWidth: "1200px", margin: "0 auto" }}>
        {data.listings.map((listing) => (
          <ClientListingView key={listing.id} listing={listing} />
        ))}
      </div>

      {/* Footer */}
      <div style={{ textAlign: "center", padding: "40px 0", color: "#999", fontSize: "12px" }}>
        Clear Sky Investment Group
      </div>
    </div>
  );
}


function ClientListingView({ listing }) {
  const l = listing;
  const narrow = useIsNarrow();

  // Financial controls state. Seeded below from what the analyst actually used for THIS
  // listing, then owned by the client — these are the inputs they are invited to play with.
  const [downPaymentPct, setDownPaymentPct] = useState(l.analysis?.down_payment_percent ?? 25);
  const [piRate, setPiRate] = useState(6.25);
  const [ioRate, setIoRate] = useState(5.75);

  // Open on the analyst's numbers.
  //
  // Both defaults above were hardcoded, so a listing the analyst had priced at a custom rate
  // showed the client a different cap rate, cash flow and IRR than the analyst was quoting —
  // and even with no override the page ignored the global rate in Settings. resolveMortgageRates
  // is the same helper the Rental Value Analysis uses, so "the analyst's rate" means exactly
  // what it means on /predict; /config/mortgage is a public route, so this page can read it.
  //
  // Seeded ONCE. Re-running on every render would stamp on the client's own edits.
  const [ratesSeeded, setRatesSeeded] = useState(false);
  useEffect(() => {
    if (ratesSeeded) return;
    let cancelled = false;
    (async () => {
      let globalConfig = null;
      try {
        const resp = await fetch(`${API_URL}/config/mortgage`);
        if (resp.ok) globalConfig = (await resp.json())?.data || null;
      } catch (e) {
        // Fall through: resolveMortgageRates supplies 6.25/5.75 when it gets nothing.
        console.error("Could not load mortgage config:", e);
      }
      if (cancelled) return;
      const resolved = resolveMortgageRates(globalConfig, l.analysis);
      setPiRate(resolved.piRate);
      setIoRate(resolved.ioRate);
      setRatesSeeded(true);
    })();
    return () => { cancelled = true; };
  }, [l.analysis, ratesSeeded]);

  // The analyst's negotiated discount. Everything financial on this page is priced off
  // `dealPrice`, not the asking price: a discount the client can see but that no number
  // reflects is worse than showing no discount at all. Asking price still appears, labelled
  // as such, so they can see both.
  // Trimmed, and belt-and-braces with the SQL: clearing the textarea stores whitespace
  // rather than an empty string, which is truthy and rendered an empty box.
  const opportunityText = (l.client_opportunity_text || "").trim();

  const askingPrice = l.price || 0;

  // The discount is a control, not a readout. Seeded ONCE from what the analyst negotiated,
  // then owned by the client the way the down payment and the rates already are — a buyer
  // asking "and at 5% off?" should not need to call anyone to find out.
  //
  // Held as a string so the field can be emptied and retyped; every consumer reads the
  // parsed number below. Nothing is written back — this page has no save path, and these
  // are what-ifs, not a revision of the analyst's work.
  const [discountInput, setDiscountInput] = useState(
    () => String(Number(l.discount_pct) > 0 ? Number(l.discount_pct) : 0)
  );
  const discountPct = Math.min(100, Math.max(0, parseFloat(discountInput) || 0));
  const dealPrice = Math.round(askingPrice * (1 - discountPct / 100));

  // Closing costs follow the client's Down Payment control — always, including when the
  // analyst saved a figure.
  //
  // Two lines on a closing bill are levied on the LOAN rather than the price: mortgage
  // recording tax (1.8–2.175% of the loan) and the lender's title policy ($2.75 per $1,000
  // of it). Both move whenever the down payment moves, so a figure pinned to whatever the
  // analyst last saved is simply wrong for a client exploring a different one — understating
  // the bill by ~$5,500 on a $1M condo at 0% down against the analyst's 25%.
  //
  // Recomputing from config alone would throw the analyst's work away: their total also
  // carries hand-edited fees, a closing-cost credit, and closing-cost items that live on the
  // checklist and are deliberately never written back onto the analysis (ClosingCostsModal
  // saves only user-added rows, id < 100). So we recompute TWICE — once at the analyst's own
  // down payment, once at the client's — and carry the gap between the analyst's saved total
  // and our baseline across as an opaque offset. At the analyst's down payment the client
  // sees their number to the dollar; away from it, only the loan-driven lines move.
  //
  // sale_type and financing come from the analyst too: "No" financing, or a co-op, means
  // there is no loan and these lines are zero, so the total correctly stops responding.
  // Config is fetched once per listing; the figure itself is derived. It used to be one
  // effect doing both, keyed on the price — which was survivable when only the down payment
  // moved it, but the discount is now a text field and that would be an HTTP request per
  // keystroke, with the answers free to arrive out of order.
  const savedClosingCosts = l.analysis?.net_closing_costs;
  const analystDpPct = l.analysis?.down_payment_percent ?? 25;
  const [closingCostsConfig, setClosingCostsConfig] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${API_URL}/config/closing_costs?property_id=${l.id}`);
        if (!resp.ok) return;
        const json = await resp.json();
        if (!json?.data || cancelled) return;
        setClosingCostsConfig(json.data);
      } catch (e) {
        // Leave it unset — the box falls back to the analyst's saved figure below.
        console.error("Could not load closing costs config:", e);
      }
    })();
    return () => { cancelled = true; };
  }, [l.id]);

  const closingCosts = useMemo(() => {
    if (!closingCostsConfig || !dealPrice) return null;
    const common = {
      // Mansion tax, transfer tax and the title policies are all levied on what is
      // actually paid, so this is the discounted price, not the asking price.
      askingPrice: dealPrice,
      buildingType: l.building_type || "Condo",
      saleType: l.analysis?.sale_type || "Resale",
      financing: l.analysis?.financing || "Yes",
      config: closingCostsConfig,
      closingCostCredit: 0,
    };
    const atClient = calculateClosingCosts({
      ...common, downPaymentPercent: downPaymentPct,
    }).netClosingCosts;

    const hasSaved = typeof savedClosingCosts === "number" && savedClosingCosts > 0;
    if (!hasSaved) return atClient;

    const atAnalyst = calculateClosingCosts({
      ...common, downPaymentPercent: analystDpPct,
    }).netClosingCosts;
    return Math.max(0, savedClosingCosts + (atClient - atAnalyst));
  }, [closingCostsConfig, dealPrice, l.building_type, l.analysis, savedClosingCosts, analystDpPct, downPaymentPct]);

  // Build mortgage config from client controls
  const mortgageConfig = {
    down_payment_percent: downPaymentPct,
    rates: {
      pi: { "30_year_fixed": piRate },
      io: { "7_1_arm": ioRate },
    },
  };

  const downPaymentAmt = Math.round(dealPrice * (downPaymentPct / 100));

  // The client page shows the AI prediction ONLY — never the ML number. Both travel in the
  // payload (the email template still reads the ML pair), so the choice is made here, once,
  // and every box below is driven from `aiRent`: Property Details' cap rate and IRR, the
  // rental analysis, and the summary block. Falling back to the ML rent when the AI has not
  // run yet is deliberate — a blank page is worse than a slightly older number.
  const aiRent = l.ai_predicted_rent || l.predicted_rent || 0;

  // The analyst's Min/Max, resolved the way the analysis form resolves them.
  //
  // Both ends were pinned to the AI rent here, so the range the desk had actually chosen --
  // a floor of the sitting tenant's rent, say, against a ceiling they typed themselves --
  // never reached the client, and Rental Value Analysis showed one number twice.
  //
  // Same calculateRentFromMode the analyst page calls, over the same four inputs, so the two
  // pages cannot disagree about what "Min" means. No mode set (or a mode whose value is
  // missing) falls back to the AI rent, which is what the page showed before.
  const rentInputs = {
    ml: l.predicted_rent || 0,
    ai: aiRent,
    tenant: l.tenant_current_rent || 0,
  };
  //
  // A SITTING TENANT OUTRANKS THE PREDICTION AT ONE END OF THE RANGE.
  //
  // A tenant in place is not a forecast — it is the rent this apartment is collecting today,
  // and it belongs in the range as a fact rather than as a fifth column the client has to
  // reconcile against the other four (which is what the analyst's page shows them as). Which
  // end it takes depends on how it compares with what the unit should fetch:
  //
  //   tenant pays ABOVE the predicted ceiling -> the in-place rent IS the top of the range,
  //   and the prediction becomes the downside ("Potential Rent" at the low end).
  //   tenant pays BELOW it -> the in-place rent is the floor, and the prediction is the
  //   upside the client is buying into.
  //
  // Either way the client reads one number they are earning now and one they could earn, so
  // the two ends are RELABELLED ("Current Rent" / "Potential Rent", see rentLabels below) --
  // "Low/High" would hide which of the two is already banked.
  //
  // Only ONE end ever moves. The other is whatever the analyst saved, untouched -- including
  // when they pinned BOTH ends to the tenant (both modes "current_tenant", which is common),
  // where the client correctly sees the same figure as Current and as Potential: the desk is
  // saying this unit is worth its in-place rent and no more. Substituting the AI number for
  // the second end to avoid the repetition would overrule that, and quietly.
  //
  // Gated on tenant_occupied, not on the rent alone: answering "no" to occupancy leaves the
  // old rent in the blob, and pricing the deal off a tenant who has gone would be the worst
  // kind of wrong — confidently, in the client's favour.
  const tenantRent = (l.tenant_occupied === "yes" && l.tenant_current_rent > 0)
    ? l.tenant_current_rent
    : 0;

  // Resolved once, to SEED the two rent fields in Financial Assumptions. From there the
  // client owns them: the Rental Value Analysis, its cap rates, cash flows and IRRs all run
  // on whatever is in those boxes. Nothing is sent back — the analyst's saved range is
  // untouched by anything that happens here.
  const seedRents = (() => {
    let low = calculateRentFromMode(l.rent_mode_low,
      { ...rentInputs, custom: l.rent_custom_low || 0 }) || aiRent;
    let high = calculateRentFromMode(l.rent_mode_high,
      { ...rentInputs, custom: l.rent_custom_high || 0 }) || aiRent;

    if (tenantRent > 0) {
      if (tenantRent > high) {
        return { low, high: tenantRent, tenantEnd: "high" };
      }
      return { low: tenantRent, high, tenantEnd: "low" };
    }
    return { low, high, tenantEnd: null };
  })();

  // Which end the tenant took, and therefore what the two boxes are called. Fixed at seed
  // time: it describes where the numbers CAME FROM, so it must not flicker as the client
  // types their own what-if into the box.
  const { tenantEnd } = seedRents;
  const rentLabels = {
    low: tenantEnd === "low" ? "Current Rent" : tenantEnd === "high" ? "Potential Rent" : "Low Rent",
    high: tenantEnd === "high" ? "Current Rent" : tenantEnd === "low" ? "Potential Rent" : "High Rent",
  };

  const [lowRentInput, setLowRentInput] = useState(() => String(Math.round(seedRents.low)));
  const [highRentInput, setHighRentInput] = useState(() => String(Math.round(seedRents.high)));
  const lowRent = Math.max(0, parseFloat(lowRentInput) || 0);
  const highRent = Math.max(0, parseFloat(highRentInput) || 0);

  // The listing's own cap rate: the High Rent in Financial Assumptions over the price
  // actually paid after any discount. Declared here because it needs both dealPrice and
  // highRent, and a const cannot be read before it is initialised.
  //
  // It MUST be highRent, not aiRent. Property Details computes its Cap Rate, RCR, Weighted
  // RCR and Asset Class RCR from highRent (via computedValues), and this feeds the
  // Neighborhood Comparison card lower down. Pinning this one to the AI rent put two
  // different RCRs for the same listing on the same page the moment a client touched the
  // rent box -- same label, same listing, different number, a few hundred pixels apart.
  //
  // What stays fixed is the DENOMINATOR. The neighborhood averages are market measurements
  // and do not move for anybody's what-if, so the ratio reads as "my scenario against the
  // market", which is the comparison worth offering.
  const listingCapRate = (dealPrice > 0 && highRent > 0)
    ? ((highRent - (l.monthly_hoa || 0) - (l.monthly_tax || 0)) * 12 / dealPrice) * 100
    : 0;

  // The team's saved out-of-pocket inputs, with the client's own down-payment choice layered
  // on top so this page's Financial Assumptions control actually drives the Out of Pocket box.
  // NOTE the three nulls. The analyst's rate is used to SEED the controls above, and must not
  // also reach the calculator: resolveMortgageRates treats a matching override as authoritative,
  // so leaving these in would pin the rate and make the client's own rate inputs inert — they
  // would type a new rate and watch nothing move. Seeding sets the starting point; the controls
  // own it from there, and they feed the calculator through `mortgageConfig` below.
  const propertyAnalysis = {
    ...(l.analysis || {}),
    mortgage_rate: null,
    payment_type: null,
    mortgage_term: null,
    net_closing_costs: closingCosts ?? (savedClosingCosts || 0),
    down_payment_percent: downPaymentPct,
    // down_payment_amount would pin the figure to whatever the analyst last saved and ignore
    // the control above it, so it is left out on purpose.
  };

  // Build property object matching what PropertyDetails expects
  const property = {
    address: l.address,
    unit_number: l.unit_number,
    price: l.price,
    asking_price: l.price,
    bedrooms: l.bedrooms,
    bathrooms: l.bathrooms,
    sqft: l.sqft,
    monthly_hoa: l.monthly_hoa || 0,
    monthly_tax: l.monthly_tax || 0,
    neighborhood: l.neighborhood,
    borough: l.borough,
    property_type: l.property_type,
    listed_date: l.listed_at,
    listing_url: l.listing_url,
    price_change: l.price_change,
    price_change_pct: l.price_change_pct,
    image_urls: l.image_urls || [],
    floor_plan_url: l.floor_plan_url,
    video_urls: l.video_urls || [],
    tour_urls: l.tour_urls || [],
    floor: l.floor,
    washer_dryer: l.washer_dryer,
    dishwasher: l.dishwasher,
    renovation: l.renovation,
    views: l.views,
    neighborhood_avg_cap_rate: l.neighborhood_avg_cap_rate,
    neighborhood_weighted_avg_cap_rate: l.neighborhood_weighted_avg_cap_rate,
    neighborhood_median_cap_rate: l.neighborhood_median_cap_rate,
    // Only ever a REAL same-size figure here: the API withholds a fallback from the client
    // payload, so this is null rather than the hood-wide number wearing a bedroom label.
    neighborhood_asset_class_cap_rate: l.neighborhood_asset_class_cap_rate,
    floor_line: l.floor_line,
    building_type: l.building_type,
    home_features: l.home_features || [],
  };

  const prediction = {
    predicted_rent: aiRent,
  };

  // low == high == the AI rent: with a single prediction there is no low/high pair to show,
  // and calculateRentalAnalysis derives its headline cap rate and IRR from highRent.
  const computedValues = {
    originalPrice: askingPrice,
    discountPct,
    discountedPrice: dealPrice,
    mlRent: aiRent,
    agentRent: aiRent,
    lowRent,
    highRent,
  };

  return (
    <div style={{ marginBottom: "40px" }}>
      {/* Property title */}
      <h2 style={{
        fontSize: narrow ? "17px" : "20px",
        fontWeight: "600",
        color: "#333",
        marginBottom: "16px",
        paddingBottom: "8px",
        borderBottom: "2px solid #000",
        // A long address is one unbroken token to the layout engine; without this it is what
        // pushes the whole card wider than the screen.
        overflowWrap: "anywhere",
      }}>
        {l.listing_url ? (
          <a href={l.listing_url} target="_blank" rel="noopener noreferrer" style={{ color: "#1976d2", textDecoration: "none" }}>
            {(l.address || "").trim()} #{l.unit_number}
          </a>
        ) : (
          `${(l.address || "").trim()} #${l.unit_number}`
        )}
      </h2>

      {/* Image Gallery */}
      <ImageGallery
        imageUrls={l.image_urls || []}
        floorPlanUrl={l.floor_plan_url}
        videoUrls={l.video_urls || []}
        tourUrls={l.tour_urls || []}
      />

      {/* Property Details — same component and same shape as /predict, so the client sees
          the analyst's page minus the internal controls. propertyAnalysis is the saved one
          (not {}), which is what makes cap rate and IRR here match the analyst's figures. */}
      <PropertyDetails
        property={property}
        prediction={prediction}
        propertyAnalysis={propertyAnalysis}
        checklistData={{}}
        computedValues={computedValues}
        showViews={false}
        labelRenovation
      />

      {/* Opportunity — the analyst's own pitch for this listing, typed on the checklist.
          Rendered only when they wrote one, so an empty field costs the client nothing.
          Sits directly under Property Details: it argues about the facts just above it. */}
      {opportunityText && (
        <div style={{
          backgroundColor: "white",
          padding: narrow ? "16px 12px" : "24px",
          borderRadius: "8px",
          marginBottom: "24px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}>
          <h2 style={{
            fontSize: "20px",
            fontWeight: "600",
            color: "#333",
            marginBottom: "16px",
            borderBottom: "2px solid #000",
            paddingBottom: "8px",
          }}>
            Opportunity
          </h2>
          {/* whiteSpace keeps the analyst's own paragraph breaks; overflowWrap stops a long
              url or address from pushing the card wider than a phone screen. */}
          <div style={{
            fontSize: narrow ? "14px" : "15px",
            lineHeight: 1.6,
            color: "#333",
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
          }}>
            {opportunityText}
          </div>
        </div>
      )}

      {/* Home Features. PropertyFeatures also renders Building Features and the listing
          description, but the payload carries neither, so only the Home Features box shows. */}
      <PropertyFeatures property={property} />

      {/* Financial Controls */}
      <div style={{
        backgroundColor: "white",
        padding: narrow ? "16px" : "20px",
        borderRadius: "8px",
        border: "2px solid #000",
        marginBottom: "20px",
      }}>
        <div style={{ fontSize: "18px", fontWeight: "700", marginBottom: "4px", color: "#333" }}>
          Financial Assumptions
        </div>
        {/* Said once, plainly. Six of these nine cells now accept typing, and a reader who
            changes the discount and watches every number below move needs to know they have
            explored a scenario, not amended an offer. */}
        <div style={{ fontSize: "13px", color: "#666", marginBottom: "16px", lineHeight: 1.5 }}>
          Every white field is yours to change — the analysis below updates as you type.
          Nothing is saved, so reload the page to return to the figures we quoted.
        </div>
        {/* The price cells share this row rather than sitting in a block of their own, so the
            box does not grow. Discount and Estimated Price are always present -- at 0% they
            say "you are being quoted the asking price", which is information, and dropping
            them left the reader unable to tell which price the controls beside them act on.

            auto-fit rather than a fixed count: nine cells in one row squeezes "Down Payment
            ($)" into three lines, and the right number of rows depends on the viewport, not
            on a number typed here. */}
        <div style={{
          display: "grid",
          gridTemplateColumns: narrow ? "1fr 1fr" : "repeat(auto-fit, minmax(150px, 1fr))",
          gap: narrow ? "12px" : "16px",
          alignItems: "end",
        }}>
          <div>
            <label style={controlLabelStyle}>Asking Price</label>
            <div style={readOnlyControlStyle}>${askingPrice.toLocaleString()}</div>
          </div>
          <div>
            <label style={controlLabelStyle}>Discount (%)</label>
            <input
              type="number"
              style={controlInputStyle}
              value={discountInput}
              onChange={(e) => setDiscountInput(e.target.value)}
              onBlur={() => setDiscountInput(String(discountPct))}
              min="0" max="100" step="0.5"
            />
          </div>
          <div>
            <label style={controlLabelStyle}>Estimated Price</label>
            <div style={{ ...readOnlyControlStyle, color: "#28a745", fontWeight: "600" }}>
              ${dealPrice.toLocaleString()}
            </div>
          </div>
          {/* The two rents the Rental Value Analysis below is built from, under the same
              names its columns carry so it is obvious which box drives which column. */}
          <div>
            <label style={controlLabelStyle}>{rentLabels.low} ($/mo)</label>
            <input
              type="number"
              style={controlInputStyle}
              value={lowRentInput}
              onChange={(e) => setLowRentInput(e.target.value)}
              onBlur={() => setLowRentInput(String(lowRent))}
              min="0" step="50"
            />
          </div>
          <div>
            <label style={controlLabelStyle}>{rentLabels.high} ($/mo)</label>
            <input
              type="number"
              style={controlInputStyle}
              value={highRentInput}
              onChange={(e) => setHighRentInput(e.target.value)}
              onBlur={() => setHighRentInput(String(highRent))}
              min="0" step="50"
            />
          </div>
          <div>
            <label style={controlLabelStyle}>Down Payment (%)</label>
            <input
              type="number"
              style={controlInputStyle}
              value={downPaymentPct}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                if (!isNaN(v)) setDownPaymentPct(Math.max(0, Math.min(100, v)));
              }}
              min="0" max="100" step="1"
            />
          </div>
          <div>
            <label style={controlLabelStyle}>Down Payment ($)</label>
            <div style={readOnlyControlStyle}>${downPaymentAmt.toLocaleString()}</div>
          </div>
          <div>
            <label style={controlLabelStyle}>P&I Rate (%)</label>
            <input
              type="number"
              style={controlInputStyle}
              value={piRate}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                if (!isNaN(v)) setPiRate(Math.max(0, Math.min(15, v)));
              }}
              min="0" max="15" step="0.125"
            />
          </div>
          <div>
            <label style={controlLabelStyle}>I/O Rate (%)</label>
            <input
              type="number"
              style={controlInputStyle}
              value={ioRate}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                if (!isNaN(v)) setIoRate(Math.max(0, Math.min(15, v)));
              }}
              min="0" max="15" step="0.125"
            />
          </div>
        </div>
      </div>

      {/* Rental Value Analysis */}
      <RentalValueAnalysis
        property={property}
        prediction={prediction}
        propertyAnalysis={propertyAnalysis}
        mlRent={aiRent}
        agentRent={aiRent}
        lowRent={lowRent}
        highRent={highRent}
        lowLabel={rentLabels.low}
        highLabel={rentLabels.high}
        discountedPrice={dealPrice}
        mortgageConfig={mortgageConfig}
      />

      {/* Out of Pocket — read-only: the renovation budget and timeline are the analyst's
          assumptions, and there is nowhere for a client's edits to be saved. */}
      <OutOfPocketSection
        property={property}
        prediction={prediction}
        propertyAnalysis={propertyAnalysis}
        // OutOfPocketSection applies the discount itself, off checklistData.discount_pct --
        // passing {} here left the down payment computed on the full asking price.
        checklistData={{ discount_pct: discountPct }}
        onUpdate={() => {}}
        readOnly
      />

      {/* AI rent: the number, the range, the reasoning — nothing else. Sits directly above
          the comps because the reasoning argues from them by name ("Anchored to COMPS 1 & 2
          ..."), so the reader meets the argument and its evidence together. */}
      <AIRentSummary
        predictedRent={l.ai_predicted_rent}
        rentLow={l.ai_rent_low}
        rentHigh={l.ai_rent_high}
        reasoning={l.ai_reasoning}
      />

      {/* RCR — the neighbourhood cap rates this listing is measured against on one side, the
          ratios themselves on the other.

          The card itself lives in RCRAnalysis, shared with the analyst's page: the two have
          to agree about these seven figures, and the analyst needs to see exactly what the
          client will. `rcr_visibility` is their per-listing choice of which rows travel --
          absent means all of them, so nothing changes for a listing nobody has touched.

          The asset-class pair is dropped when the neighbourhood has too few sales of this
          bedroom size to measure one: the API sends null rather than the hood-wide figure,
          so a client is never shown an "average for your unit type" that is really the
          average for every type. */}
      <RCRAnalysis
        capRate={listingCapRate}
        neighborhoodAvg={l.neighborhood_avg_cap_rate}
        neighborhoodWeighted={l.neighborhood_weighted_avg_cap_rate}
        neighborhoodMedian={l.neighborhood_median_cap_rate}
        assetClass={l.neighborhood_asset_class_cap_rate}
        bedrooms={l.bedrooms}
        visibility={l.rcr_visibility}
        narrow={narrow}
      />

      {/* Comparables */}
      <ComparablesSection comparables={l.comps || []} />
    </div>
  );
}

const pageStyle = {
  minHeight: "100vh",
  backgroundColor: "#f5f5f5",
  fontFamily: "Arial, sans-serif",
};

// A cap rate we do not have is omitted, not rendered as "0.00%".
const controlLabelStyle = {
  display: "block",
  fontSize: "13px",
  fontWeight: "600",
  color: "#555",
  marginBottom: "6px",
};

const controlInputStyle = {
  width: "100%",
  padding: "8px 10px",
  border: "2px solid #ddd",
  borderRadius: "6px",
  fontSize: "15px",
  fontWeight: "600",
  boxSizing: "border-box",
};

// A cell in the Financial Assumptions row the client reads rather than edits: the prices and
// the derived down-payment figure. Same box as the inputs beside it so the row reads as one,
// greyed so it is obvious which cells accept typing.
const readOnlyControlStyle = {
  ...controlInputStyle,
  backgroundColor: "#f8f9fa",
  display: "flex",
  alignItems: "center",
  // Long prices must not push the grid column wider than its share of the row.
  overflowWrap: "anywhere",
};
